import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { User, Mail, Calendar, LogOut, ShoppingBag, Heart, Package } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { getCartCount } = useCart();
  const { wishlist } = useWishlist();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 flex items-center justify-center" data-testid="profile-login-prompt">
        <div className="text-center">
          <User className="w-16 h-16 mx-auto text-[#A3A3A3] mb-6" />
          <h2 className="font-heading text-3xl mb-4">LOGIN TO VIEW PROFILE</h2>
          <p className="text-[#A3A3A3] mb-8">Please login to access your profile</p>
          <Link to="/auth" className="btn-primary inline-block" data-testid="login-btn">
            Login
          </Link>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const stats = [
    { icon: ShoppingBag, label: "Cart Items", value: getCartCount(), link: "/cart" },
    { icon: Heart, label: "Wishlist", value: wishlist.items?.length || 0, link: "/wishlist" },
    { icon: Package, label: "Orders", value: "View", link: "/orders" },
  ];

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12" data-testid="profile-page">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-2">
            ACCOUNT
          </p>
          <h1 className="font-heading text-5xl md:text-7xl tracking-tight uppercase">
            PROFILE
          </h1>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* User Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-2 border border-white/10 p-8"
          >
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 bg-[#FFD700] flex items-center justify-center">
                <span className="font-heading text-3xl text-black">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-2xl" data-testid="profile-name">{user?.name}</h2>
                <div className="flex items-center gap-2 text-[#A3A3A3] mt-2">
                  <Mail className="w-4 h-4" />
                  <span className="font-mono text-sm" data-testid="profile-email">{user?.email}</span>
                </div>
                <div className="flex items-center gap-2 text-[#A3A3A3] mt-1">
                  <Calendar className="w-4 h-4" />
                  <span className="font-mono text-sm">
                    Member since {new Date(user?.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 mt-8 pt-8">
              <button
                onClick={handleLogout}
                className="btn-outline flex items-center gap-3"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </motion.div>

          {/* Quick Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-4"
          >
            {stats.map((stat, index) => (
              <Link
                key={stat.label}
                to={stat.link}
                className="block border border-white/10 p-6 hover:border-[#FFD700]/50 transition-colors group"
                data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <stat.icon className="w-6 h-6 text-[#FFD700]" />
                    <span className="font-mono text-sm tracking-wider">{stat.label}</span>
                  </div>
                  <span className="font-heading text-2xl group-hover:text-[#FFD700] transition-colors">
                    {stat.value}
                  </span>
                </div>
              </Link>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
